// Error types for consistent error handling
export enum ErrorType {
  NETWORK = 'NETWORK_ERROR',
  AUTH = 'AUTH_ERROR',
  PERMISSION = 'PERMISSION_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  SERVER = 'SERVER_ERROR',
  DATABASE = 'DATABASE_ERROR',
  VALIDATION = 'VALIDATION_ERROR',
  RENDER = 'RENDER_ERROR',
  UNKNOWN = 'UNKNOWN_ERROR'
}

// Helper function to categorize errors based on message
export const categorizeError = (error: Error | string): ErrorType => {
  const message = typeof error === 'string' ? error : error.message;
  
  if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
    return ErrorType.NETWORK;
  }
  
  if (message.includes('authentication') || message.includes('auth') || message.includes('login') || 
      message.includes('session') || message.includes('token')) {
    return ErrorType.AUTH;
  }
  
  if (message.includes('permission') || message.includes('access') || message.includes('forbidden') || 
      message.includes('403')) {
    return ErrorType.PERMISSION;
  }
  
  if (message.includes('not found') || message.includes('404')) {
    return ErrorType.NOT_FOUND;
  }
  
  if (message.includes('server') || message.includes('500')) {
    return ErrorType.SERVER;
  }
  
  if (message.includes('database') || message.includes('query') || message.includes('SQL')) {
    return ErrorType.DATABASE;
  }
  
  if (message.includes('validation') || message.includes('invalid') || message.includes('required')) {
    return ErrorType.VALIDATION;
  }
  
  if (message.includes('render') || message.includes('component') || message.includes('React')) {
    return ErrorType.RENDER;
  }
  
  return ErrorType.UNKNOWN;
};

// Function to create a standardized error object
export const createAppError = (message: string, type: ErrorType = ErrorType.UNKNOWN): Error => {
  const error = new Error(message);
  (error as any).type = type;
  return error;
};

// Function to extract error message from various error formats
export const getErrorMessage = (error: any): string => {
  if (typeof error === 'string') {
    return error;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  if (error && typeof error === 'object') {
    if ('message' in error) {
      return error.message as string;
    }
    
    if ('error' in error && typeof error.error === 'string') {
      return error.error;
    }
    
    if ('statusText' in error) {
      return error.statusText as string;
    }
  }
  
  return 'An unknown error occurred';
};

// Function to get user-friendly error messages
export const getUserFriendlyErrorMessage = (error: any): string => {
  const message = getErrorMessage(error);
  const type = categorizeError(message);
  
  switch (type) {
    case ErrorType.NETWORK:
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    case ErrorType.AUTH:
      return 'Your session may have expired. Please sign in again.';
    case ErrorType.PERMISSION:
      return 'You don\'t have permission to access this resource.';
    case ErrorType.NOT_FOUND:
      return 'The resource you\'re looking for doesn\'t exist or has been moved.';
    case ErrorType.SERVER:
      return 'Something went wrong on our servers. We\'re working to fix it.';
    case ErrorType.DATABASE:
      return 'There was an issue with our database. Please try again later.';
    case ErrorType.VALIDATION:
      return message.includes('validation') ? message : 'The data you provided is invalid. Please check your inputs.';
    case ErrorType.RENDER:
      return 'There was a problem displaying this page. Please try refreshing.';
    default:
      return message || 'Something went wrong. Please try again later.';
  }
};